import { TrendingUp, TrendingDown, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface StatCardProps {
  title: string;
  value: string | number;
  change?: {
    value: number;
    label: string;
  };
  icon?: React.ReactNode;
  className?: string;
  loading?: boolean;
  unavailable?: boolean;
  tooltip?: string;
}

export const StatCard = ({ 
  title, 
  value, 
  change, 
  icon, 
  className,
  loading = false,
  unavailable = false,
  tooltip
}: StatCardProps) => {
  const isPositive = change && change.value >= 0;
  
  if (loading) {
    return (
      <div className={cn("stat-card group", className)}>
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-3">
            <div className="h-3 w-24 bg-muted/50 rounded animate-pulse" />
            {icon && <div className="h-5 w-5 bg-muted/50 rounded animate-pulse" />}
          </div>
          <div className="h-9 w-32 bg-muted/50 rounded animate-pulse mb-2" />
          <div className="h-3 w-20 bg-muted/50 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className={cn("stat-card group", className)}>
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</span>
            {tooltip && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-sm text-muted-foreground/60 hover:text-foreground transition-colors"
                    aria-label={`About ${title}`}
                  >
                    <Info className="w-3 h-3" />
                  </button>
                </PopoverTrigger>
                <PopoverContent side="top" align="start" className="w-80 text-xs leading-relaxed">
                  <div className="font-medium text-sm mb-1">{title}</div>
                  <div className="text-muted-foreground">{tooltip}</div>
                </PopoverContent>
              </Popover>
            )}
          </div>
          <div className="p-1.5 rounded-md bg-primary/10 border border-primary/20">
            {icon}
          </div>
        </div>
        <div className="text-3xl font-bold font-mono tracking-tight mb-1">
          {unavailable ? (
            <span className="text-muted-foreground text-base font-normal">Not enough data</span>
          ) : (
            <span className="bg-gradient-to-br from-foreground to-foreground/80 bg-clip-text text-transparent">
              {value}
            </span>
          )}
        </div>
        {change && !unavailable && (
          <div className={cn(
            "flex items-center gap-1.5 text-xs font-medium mt-2",
            isPositive ? "text-primary" : "text-destructive"
          )}>
            {isPositive ? (
              <TrendingUp className="w-3.5 h-3.5" />
            ) : (
              <TrendingDown className="w-3.5 h-3.5" />
            )}
            <span>
              {isPositive ? "+" : ""}{change.value.toFixed(2)}%
            </span>
            <span className="text-muted-foreground ml-0.5">{change.label}</span>
          </div>
        )}
      </div>
    </div>
  );
};
